/**
 * First-class tenancy — the client side. A client (the trust-owner) sees and
 * controls which firms can access their data: list the firms with access, revoke
 * one (Scenario 1: fire a firm but keep your data), grant another (Scenario 2:
 * one client, two firms).
 *
 * These run on the CLIENT's own session (the b2c owner viewing their own Firm
 * access screen) — no `useOperatorSession`. The backend gates create/revoke to
 * the client owner, so a firm acting-as cannot manage grants here.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type ApiError } from './client';

export interface FirmGrant {
  id: string;
  firmTenantId: string;
  /** Human firm name resolved server-side from firmTenantId (falls back to the id). */
  firmDisplayName: string;
  /** Firm's subdomain slug (e.g. "iprotect"), resolved server-side from
   *  firmTenantId. Null if the firm tenant has no slug or is gone. Used to
   *  match the current host subdomain against the client's active grants —
   *  see `use-neutral-branding-guard.ts`. */
  firmSlug: string | null;
  accessLevel: 'read' | 'manage';
  scope: 'all-trusts' | 'specific-trusts';
  trustIds: string[];
  status: 'active' | 'revoked' | 'expired';
  expiresAt?: string;
  createdAt: string;
  /** PER-FIRM VISIBILITY SCOPES (Paul + Beno, 2026-08-23) — what THIS firm may
   *  see of the client's ESTATE, as distinct from the trusts it administers.
   *  The server sends the RESOLVED pair (never the raw optional prefs), so the
   *  toggles have a definite state on first paint. Absent only from a backend
   *  older than 2026-08-23 — read it through `grantVisibility()`, never
   *  directly, so a mixed-deploy window renders "can see both" (the behaviour
   *  that backend actually has) instead of crashing. */
  visibility?: FirmGrantVisibility;
}

/** Resolved per-firm visibility scope. `true` = the firm can see it. */
export interface FirmGrantVisibility {
  /** The client's TOTAL PORTFOLIO VALUE — the estate-level rollup. */
  netWorth: boolean;
  /** The client's FULL ASSET LIST — specifically the personal assets held
   *  outside any trust. Trust-anchored assets are never hidden by this. */
  assetList: boolean;
}

/** The one place the FE decides what an absent `visibility` means: everything
 *  visible — matching both the server-side default and what a pre-2026-08-23
 *  backend does. Mirrors `backend/src/lib/firm-client-visibility.ts`. */
export function grantVisibility(g: Pick<FirmGrant, 'visibility'>): FirmGrantVisibility {
  return {
    netWorth: g.visibility?.netWorth !== false,
    assetList: g.visibility?.assetList !== false,
  };
}

export interface GrantsResponse {
  grants: FirmGrant[];
  correlationId: string;
}

/** The firms with access to THIS client. */
export function useGrants(opts: { enabled?: boolean } = {}) {
  return useQuery<GrantsResponse>({
    queryKey: ['grants', 'list'],
    queryFn: ({ signal }) => api<GrantsResponse>('/api/v1/grants', { signal }),
    enabled: opts.enabled ?? true,
  });
}

/** Revoke a firm's access (Scenario 1). Immediate server-side. */
export function useRevokeGrant() {
  const qc = useQueryClient();
  return useMutation<{ revoked: boolean; grantId: string }, ApiError, { grantId: string }>({
    mutationFn: ({ grantId }) =>
      api<{ revoked: boolean; grantId: string }>(`/api/v1/grants/${grantId}/revoke`, {
        method: 'POST',
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['grants'] });
    },
  });
}

/**
 * Set what ONE firm may see of this client's estate (Paul + Beno, 2026-08-23 —
 * "tick what they can see and can't see").
 *
 * PATCH /v1/grants/{id}/visibility. Client-owner only, server-gated exactly like
 * revoke — a firm acting-as cannot re-scope itself. Send either key or both; an
 * omitted key leaves that decision untouched, so two toggles can't clobber each
 * other. The response carries the SERVER's resolved pair, which is what the UI
 * renders — never an optimistic local guess about a privacy control.
 */
export function useSetGrantVisibility() {
  const qc = useQueryClient();
  return useMutation<
    { grantId: string; visibility: FirmGrantVisibility },
    ApiError,
    { grantId: string; netWorth?: boolean; assetList?: boolean }
  >({
    mutationFn: ({ grantId, ...body }) =>
      api<{ grantId: string; visibility: FirmGrantVisibility }>(
        `/api/v1/grants/${grantId}/visibility`,
        { method: 'PATCH', body },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['grants'] });
    },
  });
}

/**
 * A tenant id, as opposed to a portal slug.
 *
 * CLB-10 (2026-08-27). `POST /v1/grants` has accepted EITHER `firmTenantId` or
 * `firmSlug` since the human-reference work (`grants-create.ts`: "Nobody reads
 * [a UUID] over a phone, and nobody notices a typo in it"), and the firm-side
 * surfaces hand out the SLUG because it is the readable one. This client only
 * ever sent `firmTenantId`, so a client who typed the reference their firm
 * actually gave them got `INVALID_FIRM` — "that identifier doesn't match a
 * registered firm on Elreign" — for a perfectly correct reference. The two
 * halves of the flow were built against different fields and nobody had walked
 * it end to end.
 *
 * Deciding here rather than at the call site keeps ONE rule: every surface that
 * asks a person for a firm reference gets the same both-spellings behaviour,
 * and none of them has to know which shape it was handed. A slug is
 * slugify(firm name) and is never UUID-shaped, so this cannot misroute.
 */
const TENANT_ID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Grant a firm access to this client (Scenario 2). */
export function useGrantFirm() {
  const qc = useQueryClient();
  return useMutation<
    { grantId: string; firmTenantId: string; accessLevel: 'read' | 'manage' },
    ApiError,
    { firmTenantId: string; accessLevel: 'read' | 'manage' }
  >({
    mutationFn: ({ firmTenantId, accessLevel }) => {
      const reference = firmTenantId.trim();
      // Exactly one of the two goes on the wire — sending both is a 400
      // (FIRM_REFERENCE_AMBIGUOUS), deliberately, so there is no silent
      // precedence rule to get wrong.
      const body = TENANT_ID_RE.test(reference)
        ? { firmTenantId: reference, accessLevel }
        : { firmSlug: reference.toLowerCase(), accessLevel };
      return api<{ grantId: string; firmTenantId: string; accessLevel: 'read' | 'manage' }>(
        '/api/v1/grants',
        { method: 'POST', body },
      );
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['grants'] });
    },
  });
}
